import React, {useCallback, useEffect, useMemo, useState} from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advanced from 'dayjs/plugin/advancedFormat'
import {useNavigate, useLocation} from 'react-router-dom'
import {Controller, useForm} from 'react-hook-form'
import * as yup from 'yup'
import {yupResolver} from '@hookform/resolvers/yup'
import {toast} from 'react-toastify'
import rawCountryStates from 'iso3166-2-db/i18n/en'
import Skeleton, {SkeletonTheme} from 'react-loading-skeleton'
import {useAuth0} from '@auth0/auth0-react'
import AwesomeDebouncePromise from 'awesome-debounce-promise'
import {PhoneNumberUtil} from 'google-libphonenumber'
import validator from 'validator'
import {
  Input,
  Button,
  Select,
  ParagraphText,
  RadioGroup,
  Checkbox,
  Radio,
} from '@thryvlabs/maverick'
import 'react-calendar/dist/Calendar.css'

import {useAuthClient} from '../utils/use-auth-client'
import {useAsync} from '../utils/use-async'
import {LIST_OF_STATES} from '../utils/constants'
import {dateTimeConverter} from '../utils/timezones'
import {
  loadGooglePlaceLibScript,
  getPlacePredictions,
  getPlaceDetails,
  getPlaceId,
} from '../utils/google-places-helpers'
import {useUserRoles} from '../utils/use-user-roles'
import {useTrackLR} from '../utils/use-track-lr'

import {DateTimeModal} from '../components/date-time-modal'
import {MainContainer, FormContainer} from '../components/ui/new-demo'
import {useUserFeatures} from '../context/user-features-context'
import {addDays} from 'date-fns'
import {scrollAppTo} from '../utils/app-scroll'

const phoneUtil = PhoneNumberUtil.getInstance()

const getPlacePredictionsDebounce = AwesomeDebouncePromise(
  getPlacePredictions,
  300,
)
const PPIL_ROLES = [
  'premise',
  'national',
  'premisedemo',
  'insidedemo',
  'leadconverter',
  'inside',
]

const PP_TSS_ROLES = ['premisepilot', 'tss', 'admin', 'inside']

const REGIONS = [
  {
    name: 'East',
    value: 'pn107eljth7kyj8y',
  },
  {
    name: 'MidWest',
    value: 'juh39ircg7jr5xbt',
  },
  {
    name: 'South',
    value: '6v251tu1l7h216xz',
  },
  {
    name: 'Inside Sales',
    value: 'yhjr2kbrar2la7s9',
  },
  {
    name: 'Coastal',
    value: 'pn107eljth7kyj8y',
  },
]

const LEGACY_REGIONS = [
  ...REGIONS,
  {
    name: 'Eastern',
    value: 'pn107eljth7kyj8y',
  },
  {
    name: 'Midwestern',
    value: 'juh39ircg7jr5xbt',
  },
  {
    name: 'Southern',
    value: '6v251tu1l7h216xz',
  },
  {
    name: 'Insidesales',
    value: 'yhjr2kbrar2la7s9',
  },
]

const PLACE_DETAIL_FIELDS = ['name', 'address_components', 'formatted_address']

const REQUIRED_FIELD_MESSAGE = 'This field is required'
const REQUIRED_OPTION_MESSAGE = 'Please select an option'
const MIN_CHARACTERS_REQUIRED = 'Must be at least 3 characters'
const MAX_CHARACTERS_REQUIRED = 'Must be at most 120 characters'
const MOBILE_AREA_CODES = ['021', '022', '027']

const SCHEMA = yup.object().shape({
  prospectsFirstName: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .min(3, MIN_CHARACTERS_REQUIRED)
    .max(120, MAX_CHARACTERS_REQUIRED),
  prospectsLastName: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .min(3, MIN_CHARACTERS_REQUIRED)
    .max(120, MAX_CHARACTERS_REQUIRED),
  prospectsCompany: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .min(3, MIN_CHARACTERS_REQUIRED)
    .max(120, MAX_CHARACTERS_REQUIRED),
  prospectsStreet: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .min(3, MIN_CHARACTERS_REQUIRED)
    .max(120, MAX_CHARACTERS_REQUIRED),
  prospectsCity: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .min(3, MIN_CHARACTERS_REQUIRED)
    .max(120, MAX_CHARACTERS_REQUIRED),
  prospectsCountry: yup.object().shape({
    name: yup.string().required(REQUIRED_OPTION_MESSAGE),
    value: yup.string().required(REQUIRED_OPTION_MESSAGE),
  }),
  prospectsState: yup.object().when('prospectsStreet', {
    is: value => value && value.length > 0,
    then: schema =>
      schema.shape({
        name: yup.string().required(REQUIRED_OPTION_MESSAGE),
        value: yup.string().required(REQUIRED_OPTION_MESSAGE),
      }),
    otherwise: schema =>
      schema.shape({
        name: yup.string(),
        value: yup.string(),
      }),
  }),
  prospectsMobilePhone: yup
    .string()
    .test(
      'validate-phone-format',
      'Incorrect phone number format',
      function (value) {
        try {
          if (!value) {
            return false
          }
          const country =
            this.options.context.componentProps.selectedCountry.value
          value = value.replace(/[^\d]/g, '')
          const parsedPhone = phoneUtil.parse(value, country)
          const isValidNumber = phoneUtil.isValidNumber(parsedPhone)
          if (
            !isValidNumber &&
            country === 'NZ' &&
            value.length === 9 &&
            MOBILE_AREA_CODES.includes(value.substring(0, 3))
          ) {
            return true
          }
          return isValidNumber
        } catch {
          return false
        }
      },
    ),
  prospectsPostalCode: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .test(
      'validate-postCode-format',
      'Incorrect post code format',
      function (value) {
        if (!value) {
          return false
        }
        const country =
          this.options.context.componentProps.selectedCountry.value
        if (country === 'BB') {
          return value.match(/^[A-Z]{2}\d{5}$/)
        }
        if (country === 'KY') {
          return value.match(/^[A-Za-z]{2}\d-?\d{4}$/)
        }
        return validator.isPostalCode(value, country)
      },
    ),
  prospectsEmailAddress: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .email('Must be a valid email'),
  contactNumber: yup
    .string()
    .test(
      'validate-phone-format',
      'Incorrect phone number format',
      function (value) {
        try {
          if (!value) {
            return false
          }
          const country =
            this.options.context.componentProps.selectedCountry.value
          value = value.replace(/[^\d]/g, '')
          const parsedPhone = phoneUtil.parse(value, country)
          const isValidNumber = phoneUtil.isValidNumber(parsedPhone)
          if (
            !isValidNumber &&
            country === 'NZ' &&
            value.length === 9 &&
            MOBILE_AREA_CODES.includes(value.substring(0, 3))
          ) {
            return true
          }
          return isValidNumber
        } catch {
          return false
        }
      },
    ),
  salesforceCustomerID: yup.string().nullable(),
  scheduledDateTime: yup.string().required(REQUIRED_FIELD_MESSAGE),
})
const YELLO_USER_SCHEMA = yup.object().shape({
  yelloAccountID: yup.string().required(REQUIRED_FIELD_MESSAGE),
})
const AUSTRALIA_NZ_USER_SCHEMA = yup.object().shape({
  sensisSalesforceCustomerID: yup.string(),
})
const REGIONS_SCHEMA = yup.object().shape({
  prospectsRegion: yup.object().shape({
    name: yup.string().required(REQUIRED_OPTION_MESSAGE),
    value: yup.string().required(REQUIRED_OPTION_MESSAGE),
  }),
})

const BUSINESS_OPPORTUNITY_OPTIONS = [
  'Improving efficiency with customer inquiry follow-ups',
  'Preventing potential revenue loss due to lack of a nurture process',
  'Generating repeat and referral business',
  'Strengthening operational foundation',
  'Looking to grow customer base and generate revenue',
  'No challenge or opportunity discovered, but open to discussing new solutions',
]
const CURRENT_SOFTWARE_OPTIONS = [
  'Jobber',
  'Housecall Pro',
  'ServiceTitan',
  'GoHighLevel',
  'Pen & Paper / Excel',
  'Thryv Business Center',
  'Thryv Marketing Center',
  'Not discussed',
]
const TIMELINE_INTENT_OPTIONS = [
  'Comparing prices',
  'New to research',
  'Ready for immediate implementation',
  'General curiosity',
  'Not discussed',
]

/**
 * @param {Array<string>} roles
 * @returns
 */
function getRadioCountrySelection(roles) {
  const RADIO_COUNTRY_SELECTION = [{name: 'United States', value: 'US'}]
  const formattedRoles = roles.map(role =>
    role.toLowerCase().replace(/\s/g, ''),
  )
  // Australia user can only select Australia
  if (formattedRoles.some(role => role === 'australia')) {
    return [{name: 'Australia', value: 'AU'}]
  }
  // New Zealand user can only select New Zealand
  if (formattedRoles.some(role => role === 'newzealandsales')) {
    return [{name: 'New Zealand', value: 'NZ'}]
  }
  // InsideRep can only select Australia and New Zealand
  if (formattedRoles.some(role => role === 'inside')) {
    return [
      {name: 'Australia', value: 'AU'},
      {name: 'New Zealand', value: 'NZ'},
    ]
  }
  if (PPIL_ROLES.some(role => formattedRoles.includes(role))) {
    RADIO_COUNTRY_SELECTION.push({name: 'Canada', value: 'CA'})
    return RADIO_COUNTRY_SELECTION
  }
  if (formattedRoles.some(role => ['tss', 'admin'].includes(role))) {
    RADIO_COUNTRY_SELECTION.push(
      {name: 'Canada', value: 'CA'},
      {name: 'Australia', value: 'AU'},
      {name: 'New Zealand', value: 'NZ'},
    )
    return RADIO_COUNTRY_SELECTION
  } else {
    RADIO_COUNTRY_SELECTION.push(
      {name: 'Canada', value: 'CA'},
      {name: 'Australia', value: 'AU'},
      {name: 'New Zealand', value: 'NZ'},
      {name: 'Barbados', value: 'BB'},
      {name: 'Cayman Islands', value: 'KY'},
    )
  }
  return RADIO_COUNTRY_SELECTION
}

/**
 * @param {Array<string>} roles
 * @returns {Array<{ name: string, value: string }>}
 */
function getRegionOptions(roles) {
  const regions = [...REGIONS]
  if (roles.some(role => ['admin', 'tss'].includes(role.toLowerCase()))) {
    regions.push({
      name: 'CSM',
      value: 'hemko6kmapjhphs0',
    })
    return regions
  }
  return regions
}

const DEFAULT_VALUES = {
  contactNumber: '',
  demoConductedInSpanish: false,
  prospectsStreet: '',
  prospectsCity: '',
  prospectsCompany: '',
  prospectsEmailAddress: '',
  prospectsFirstName: '',
  prospectsLastName: '',
  prospectsMobilePhone: '',
  prospectsState: {},
  prospectsCountry: {},
  prospectsPostalCode: '',
  salesforceCustomerID: '',
  product: '',
  timeZoneId: null,
  timeZoneName: '',
  serviceId: '',
  businessOpportunity: [],
  businessOpportunityOther: '',
  currentSoftwareStack: [],
  currentSoftwareStackOther: '',
  timelineBuyingIntent: [],
}

const setShowAlignmentCallModal = ({
  roles,
  selectedCountry,
  isAustraliaUser,
  isNZUser,
  isInsideUser,
  isPremisePilotUser,
  isAdminUser,
}) => {
  // Show alignment modal for Inside, Australia, New Zealand users, PremisePilot users, and Admin users in AU/NZ
  if (
    (isInsideUser ||
      isAustraliaUser ||
      isNZUser ||
      isPremisePilotUser ||
      isAdminUser) &&
    ['AU', 'NZ'].includes(selectedCountry)
  ) {
    return true
  }

  // Hide for all other cases
  return false
}

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advanced)

function getCompanyLocationInfo(googleAddressComponents) {
  const result = {}
  googleAddressComponents.forEach(({types, long_name}) => {
    switch (types[0]) {
      case 'route':
        if (result['prospectsStreet']) {
          result[
            'prospectsStreet'
          ] = `${result['prospectsStreet']} ${long_name}`
          break
        }
        result['prospectsStreet'] = long_name
        break
      case 'locality':
        result['prospectsCity'] = long_name
        break
      case 'street_number':
        if (result['prospectsStreet']) {
          result[
            'prospectsStreet'
          ] = `${long_name} ${result['prospectsStreet']}`
          break
        }
        result['prospectsStreet'] = long_name
        break
      case 'administrative_area_level_1':
        result['prospectsState'] = long_name
        break
      case 'country':
        result['prospectsCountry'] = long_name
        break
      case 'postal_code':
        result['prospectsPostalCode'] = long_name
        break
      default:
        break
    }
  })
  return result
}

function NewDemo() {
  const RADIO_LANGUAGE_PREFERENCES = [
    {name: 'Yes', value: true},
    {name: 'No', value: false},
  ]
  const {roles} = useUserRoles()

  // Format roles early so we can use them in initial state
  const formattedRoles = roles.map(role =>
    role.toLowerCase().replace(/\s/g, ''),
  )
  const IS_AUSTRALIA_USER = formattedRoles.includes('australia')
  const IS_NZ_USER = formattedRoles.includes('newzealandsales')
  const IS_INSIDE_USER = formattedRoles.includes('inside')

  // Determine initial country based on user role
  const getInitialCountry = () => {
    if (IS_AUSTRALIA_USER) {
      return {name: 'Australia', value: 'AU'}
    }
    if (IS_NZ_USER) {
      return {name: 'New Zealand', value: 'NZ'}
    }
    if (IS_INSIDE_USER) {
      return {name: 'Australia', value: 'AU'}
    }
    return {name: 'United States', value: 'US'}
  }

  const [isFormLoading, setIsFormLoading] = useState(false)
  const [demoLanguagePreference, setDemoLanguagePreference] = useState(
    RADIO_LANGUAGE_PREFERENCES[1],
  )
  const [alignmentLanguagePreference, setAlignmentLanguagePreference] =
    useState(RADIO_LANGUAGE_PREFERENCES[1])
  const [selectedCountry, setSelectedCountry] = useState(getInitialCountry())
  const [listOfStates, setListOfStates] = useState([])
  const [selectedRegion, setSelectedRegion] = useState({
    name: '',
    value: '',
  })
  const [selectedState, setSelectedState] = useState({
    name: '',
    value: '',
  })
  const [searchedState, setSearchedState] = useState('')
  const [resetSelectedState, setResetSelectedState] = useState(false)
  const [alignmentStartDateTime, setAlignmentStartDateTime] = useState(null)
  const [demoStartDateTime, setDemoStartDateTime] = useState(null)
  const [selectedTimezone, setSelectedTimezone] = useState(null)
  const [dateTime, setDateTime] = useState(null)
  const [alignmentScheduledDateTime, setAlignmentScheduledDateTime] =
    useState(null)
  const [selectedHour, setSelectedHour] = useState(null)
  const [demoCallStaffId, setDemoCallStaffId] = useState(null)
  const [demoRequestScheduled, setDemoRequestScheduled] = useState(false)
  const [showProspectRegionInput, setShowProspectRegionInput] = useState(true)
  const [availableTimeOptions, setAvailableTimeOptions] = useState({
    demoConductedInSpanish: false,
    prospectsCountry: getInitialCountry().name,
    isMarketingPlatform: false,
    isFormerVivialCustomer: false,
    regionId: null,
    isMarketingCenter: null,
    isKeapDemo: false,
    isWorkforceCenterDemo: false,
  })
  const [availableAlignmentTimeOptions, setAvailableAlignmentTimeOptions] =
    useState({
      demoConductedInSpanish: false,
      prospectsCountry: getInitialCountry().name,
      isMarketingPlatform: false,
      isFormerVivialCustomer: false,
      regionId: null,
      isMarketingCenter: null,
      isKeapDemo: false,
      isWorkforceCenterDemo: false,
    })
  const [prospectsCompanyName, setProspectsCompanyName] = useState('')
  const [companyPredictions, setCompanyPredictions] = useState([])
  const [selectedPlaceId, setSelectedPlaceId] = useState(null)
  const [noAlignmentTimesAvailable, setNoAlignmentTimesAvailable] =
    useState(false)
  const {Demo_Region_Selection: demoRegionSelectionFlag} = useUserFeatures()
  const [keapDemo, setKeapDemo] = useState(false)
  const [workforceCenterDemo, setWorkforceCenterDemo] = useState(false)
  const {user} = useAuth0()
  const location = useLocation()
  const client = useAuthClient()
  const navigate = useNavigate()
  const {run} = useAsync({
    status: 'pending',
  })
  const isUsSpanishOptionAvailable = selectedCountry.value === 'US'

  let recordId
  if (location.state) {
    recordId = location.state.recordId
  }

  const IS_YELLO_USER = formattedRoles.includes('yello')
  const IS_PREMISE_USER =
    formattedRoles.includes('premise') || formattedRoles.includes('national')
  const IS_PPIL_USER = formattedRoles.some(role => PPIL_ROLES.includes(role))
  const IS_PP_TSS_USER = formattedRoles.some(role =>
    PP_TSS_ROLES.includes(role),
  )
  const HIDE_SPECIAL_DEMOS =
    IS_PPIL_USER ||
    IS_PP_TSS_USER ||
    IS_INSIDE_USER ||
    IS_AUSTRALIA_USER ||
    IS_NZ_USER

  // Initialize selectedCenterType based on user role
  const getInitialCenterType = () => {
    if (IS_INSIDE_USER || IS_AUSTRALIA_USER || IS_NZ_USER || IS_PP_TSS_USER) {
      return 'keap,bc,mc'
    }
    return 'keap,bc'
  }
  const [selectedCenterType, setSelectedCenterType] = useState(
    getInitialCenterType(),
  )

  const SHOW_ALIGNMENT_CALL_MODAL = setShowAlignmentCallModal({
    roles,
    selectedCountry: selectedCountry.value,
    isAustraliaUser: IS_AUSTRALIA_USER,
    isNZUser: IS_NZ_USER,
    isInsideUser: IS_INSIDE_USER,
    isPremisePilotUser:
      IS_PP_TSS_USER && formattedRoles.includes('premisepilot'),
    isAdminUser: formattedRoles.includes('admin'),
  })

  // let showRadioButtons = true

  // if (IS_PREMISE_USER) {
  //   // setShowRadioButtons(true)
  // }

  const showRadioButtons =
    formattedRoles.includes('premise') ||
    formattedRoles.includes('national') ||
    formattedRoles.includes('national')

  const handleCenterTypeChange = value => {
    setSelectedCenterType(value)
    if (value === 'keap,bc') {
      if (workforceCenterDemo) {
        setValue('workforceCenterDemo', false)
        setWorkforceCenterDemo(false)
      }
      setValue('keapDemo', true)
      setKeapDemo(true)
      // For Premise users, allow US and CA selection
      // For InsideRep users, allow AU and NZ selection
      if (!IS_PREMISE_USER && !IS_INSIDE_USER) {
        setSelectedCountry(RADIO_COUNTRY_SELECTION[0])
      }
      setValue('product', 'Business Center / Marketing Center')
      updateAvailableAlignmentTimeOptions({
        name: 'isKeapDemo',
        value: true,
      })
      updateAvailableTimeOptions({
        name: 'isKeapDemo',
        value: true,
      })
    } else if (value === 'wc') {
      setValue('workforceCenterDemo', true)
      setWorkforceCenterDemo(true)
      if (keapDemo) {
        setValue('keapDemo', false)
        setKeapDemo(false)
      }
      // Workforce Center is only for US
      setSelectedCountry({name: 'United States', value: 'US'})
      setValue('prospectsCountry', {name: 'United States', value: 'US'})
      updateAvailableAlignmentTimeOptions({
        name: 'isWorkforceCenterDemo',
        value: true,
      })
      updateAvailableTimeOptions({
        name: 'isWorkforceCenterDemo',
        value: true,
      })
    } else if (value === 'keap,bc,mc') {
      setValue('keapDemo', true)
      setKeapDemo(true)
      if (workforceCenterDemo) {
        setValue('workforceCenterDemo', false)
        setWorkforceCenterDemo(false)
      }
      setSelectedCountry(RADIO_COUNTRY_SELECTION[0])
      setValue('product', 'Marketing Center')
    } else if (value === 'mc') {
      if (workforceCenterDemo) {
        setValue('workforceCenterDemo', false)
        setWorkforceCenterDemo(false)
      }
      if (keapDemo) {
        setValue('keapDemo', false)
        setKeapDemo(false)
      }
      setValue('product', 'Marketing Center')
      updateAvailableTimeOptions({
        name: 'isMarketingCenter',
        value: true,
      })
      updateAvailableAlignmentTimeOptions({
        name: 'isMarketingCenter',
        value: true,
      })
    }
  }

  const RADIO_COUNTRY_SELECTION = getRadioCountrySelection(formattedRoles)
  const setSchema = () => {
    let result = SCHEMA
    if (IS_YELLO_USER) {
      result = result.concat(YELLO_USER_SCHEMA)
    }
    if (IS_AUSTRALIA_USER || IS_NZ_USER) {
      result = result.concat(AUSTRALIA_NZ_USER_SCHEMA)
    }
    if (
      demoRegionSelectionFlag &&
      selectedCountry.value === 'US' &&
      !formattedRoles.includes('premisedemo') &&
      !formattedRoles.includes('tss') &&
      !formattedRoles.includes('admin')
    ) {
      result = result.concat(REGIONS_SCHEMA)
    }
    if (['US', 'CA'].includes(selectedCountry.value)) {
      result = result.concat(
        yup.object().shape({
          businessOpportunity: yup
            .array()
            .of(yup.string())
            .test('bo-required', REQUIRED_FIELD_MESSAGE, function (list) {
              const {businessOpportunityOther} = this.parent
              return (
                (Array.isArray(list) && list.length > 0) ||
                !!businessOpportunityOther
              )
            }),
          businessOpportunityOther: yup.string().nullable(),
          currentSoftwareStack: yup
            .array()
            .of(yup.string())
            .test('css-required', REQUIRED_FIELD_MESSAGE, function (list) {
              const {currentSoftwareStackOther} = this.parent
              return (
                (Array.isArray(list) && list.length > 0) ||
                !!currentSoftwareStackOther
              )
            }),
          currentSoftwareStackOther: yup.string().nullable(),
          timelineBuyingIntent: yup
            .array()
            .of(yup.string())
            .min(1, REQUIRED_FIELD_MESSAGE),
        }),
      )
    }
    return result
  }

  if (!document.querySelector('#google-maps-place')) {
    loadGooglePlaceLibScript()
  }

  const {
    register,
    handleSubmit,
    control,
    formState: {errors},
    setValue,
    trigger,
    watch,
    getValues,
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(setSchema()),
    context: {componentProps: {selectedCountry}},
  })

  const toggleArrayValue = (field, value) => {
    const current = getValues(field) || []
    const exists = current.includes(value)
    const next = exists ? current.filter(v => v !== value) : [...current, value]
    setValue(field, next, {shouldValidate: true})
  }

  const [
    watchFirstName,
    watchLastName,
    watchProspectsMobilePhone,
    watchProspectsStreet,
    watchProspectsCity,
    watchProspectsPostalCode,
    watchProduct,
    watchProspectsState,
    watchProspectsDate,
    watchAlignmentScheduledDateTime,
    watchProspectsEmailAddress,
    watchProspectsCompany,
    watchProspectsRegion,
    watchBusinessOpportunity,
    watchCurrentSoftwareStack,
    watchTimelineBuyingIntent,
    watchBusinessOpportunityOther,
    watchCurrentSoftwareStackOther,
  ] = watch([
    'prospectsFirstName',
    'prospectsLastName',
    'prospectsMobilePhone',
    'prospectsStreet',
    'prospectsCity',
    'prospectsPostalCode',
    'product',
    'prospectsState',
    'prospectsDate',
    'alignmentScheduledDateTime',
    'prospectsEmailAddress',
    'prospectsCompany',
    'prospectsRegion',
    'businessOpportunity',
    'currentSoftwareStack',
    'timelineBuyingIntent',
    'businessOpportunityOther',
    'currentSoftwareStackOther',
  ])
  const marketingCenterDemo = watchProduct === 'Marketing Center'

  // Check if all required questions are answered
  // Questions are ONLY required for US/CA
  const areQuestionsAnswered = useMemo(() => {
    // Questions only required for US/CA
    if (!['US', 'CA'].includes(selectedCountry.value)) {
      return true // Questions not required for AU/NZ and other countries
    }

    const hasBusinessOpportunity =
      (watchBusinessOpportunity && watchBusinessOpportunity.length > 0) ||
      (watchBusinessOpportunityOther &&
        watchBusinessOpportunityOther.trim().length > 0)
    const hasCurrentSoftware =
      (watchCurrentSoftwareStack && watchCurrentSoftwareStack.length > 0) ||
      (watchCurrentSoftwareStackOther &&
        watchCurrentSoftwareStackOther.trim().length > 0)
    const hasTimelineIntent =
      watchTimelineBuyingIntent && watchTimelineBuyingIntent.length > 0

    return hasBusinessOpportunity && hasCurrentSoftware && hasTimelineIntent
  }, [
    selectedCountry.value,
    watchBusinessOpportunity,
    watchBusinessOpportunityOther,
    watchCurrentSoftwareStack,
    watchCurrentSoftwareStackOther,
    watchTimelineBuyingIntent,
  ])

  const demoSchedulerFields = [
    watchFirstName &&
      watchLastName &&
      watchProspectsMobilePhone &&
      watchProspectsEmailAddress &&
      selectedCountry.name.length > 0 &&
      watchProspectsStreet &&
      watchProspectsCity &&
      watchProspectsState &&
      watchProspectsState.value &&
      watchProspectsPostalCode &&
      watchProspectsCompany,
  ]

  const regionCondition =
    demoRegionSelectionFlag &&
    showProspectRegionInput &&
    selectedCountry.value === 'US' &&
    !formattedRoles.includes('premisedemo') &&
    !formattedRoles.includes('tss') &&
    !formattedRoles.includes('admin')
  const demoSchedulerEnabled = regionCondition
    ? demoSchedulerFields[0] &&
      watchProspectsRegion &&
      watchProspectsRegion.value
    : demoSchedulerFields[0]

  const customEvent = watchProspectsEmailAddress &&
    prospectsCompanyName && {
      key: '************',
      companyName: prospectsCompanyName,
      companyEmail: watchProspectsEmailAddress,
    }
  useTrackLR(customEvent)

  const handleCompanySearch = e => {
    if (e.type === 'click') {
      const companyName = e.target.innerText
      const {placeId} = companyPredictions.find(
        p =>
          p.description.toLowerCase().replace(/ /g, '') ===
          companyName.toLowerCase().replace(/ /g, ''),
      )
      setSelectedPlaceId(placeId)
      return
    }
    const query = e.target.value
    setValue('prospectsCompany', query)
    setProspectsCompanyName(query)
  }

  const handleCompanySearchBlur = e => {
    if (e.relatedTarget?.tagName !== 'BUTTON') {
      setCompanyPredictions([])
    }
  }

  const getStates = useCallback(
    countryIso => {
      const states = rawCountryStates[countryIso].regions
        .map(({iso, name}) => ({
          name,
          value: iso || name,
        }))
        .sort((a, b) => (a.name > b.name ? 1 : -1))
      setListOfStates(states)
    },
    [setListOfStates],
  )

  const setState = (value, reset) => {
    setSelectedState(value)
    setValue('prospectsState', {...value})
    if (reset) {
      setResetSelectedState(true)
    } else {
      trigger('prospectsState')
    }
  }
  const saveDemoCallData = ({
    longDate,
    selectedStaffId,
    selectedHourTimeStamp,
    ISODate,
    timeZoneId,
    timeZoneName,
    startDateTime,
    selectedServiceId,
    selectedLanguagePreference,
  }) => {
    setDateTime(ISODate)
    setValue('prospectsDate', longDate)
    setValue('staffId', selectedStaffId)
    setDemoCallStaffId(selectedStaffId)
    setSelectedHour(selectedHourTimeStamp)
    setDemoStartDateTime(startDateTime)
    setValue('scheduledDateTime', ISODate)
    setValue('timeZoneId', timeZoneId)
    setValue('timeZoneName', timeZoneName)
    setSelectedTimezone(timeZoneId)
    setValue('serviceId', selectedServiceId)
    trigger(['scheduledDateTime', 'prospectsDate', 'staffId'])
    setValue('demoConductedInSpanish', selectedLanguagePreference.value)
  }

  const saveAlignmentCallData = ({
    ISODate,
    alignmentCallTimesAvailable,
    startDateTime,
    selectedServiceId,
    selectedLanguagePreference,
  }) => {
    if (!alignmentCallTimesAvailable) {
      setNoAlignmentTimesAvailable(true)
    } else {
      setValue('alignmentScheduledDateTime', ISODate)
      setAlignmentScheduledDateTime(ISODate)
      setAlignmentStartDateTime(startDateTime)
      setValue('alignmentServiceId', selectedServiceId)
      trigger('alignmentScheduledDateTime')
      setValue('demoConductedInSpanish', selectedLanguagePreference.value)
    }
  }

  const updateAvailableTimeOptions = ({name, value}) => {
    const options = {
      ...availableTimeOptions,
      [name]: value,
    }
    setAvailableTimeOptions(options)
    setDateTime(null)
  }

  const updateAvailableAlignmentTimeOptions = ({name, value}) => {
    const options = {
      ...availableAlignmentTimeOptions,
      [name]: value,
    }
    setAvailableAlignmentTimeOptions(options)
  }

  const submitForm = async formData => {
    // Validate required questions for US and CA
    if (['US', 'CA'].includes(selectedCountry.value)) {
      const hasBusinessOpportunity =
        (formData.businessOpportunity &&
          formData.businessOpportunity.length > 0) ||
        formData.businessOpportunityOther
      const hasCurrentSoftware =
        (formData.currentSoftwareStack &&
          formData.currentSoftwareStack.length > 0) ||
        formData.currentSoftwareStackOther
      const hasTimelineIntent =
        formData.timelineBuyingIntent &&
        formData.timelineBuyingIntent.length > 0

      if (
        !hasBusinessOpportunity ||
        !hasCurrentSoftware ||
        !hasTimelineIntent
      ) {
        // Scroll to top to make error visible
        scrollAppTo({top: 0, behavior: 'smooth'})

        // Show error toast
        toast.error(
          'Please answer all required questions: Business Opportunity, Current Software Stack, and Timeline & Buying Intent',
          {
            autoClose: 5000,
            closeOnClick: true,
            theme: 'colored',
          },
        )

        // Log for debugging
        console.error('Validation failed:', {
          hasBusinessOpportunity,
          hasCurrentSoftware,
          hasTimelineIntent,
          formData,
        })

        return
      }
    }

    setIsFormLoading(true)
    const DATA = {
      ...formData,
      thryvBookingDate: demoStartDateTime,
      demoType: keapDemo ? 'keap' : 'thryv',
    }
    if (alignmentStartDateTime) {
      DATA.alignmentThryvBookingDate = alignmentStartDateTime
    }
    if (keapDemo) {
      DATA.product = 'Keap Demo Request'
    }
    if (workforceCenterDemo) {
      DATA.product = 'Workforce Center Demo Request'
    }
    // A failed place lookup must not abort the whole demo-request submit —
    // the place URL is metadata. Fall back to the id the user already picked.
    let placeId
    try {
      placeId = await getPlaceId({
        fields: ['place_id'],
        query: `${DATA.prospectsCompany} ${DATA.prospectsStreet} ${DATA.prospectsPostalCode} ${DATA.propspectsCity} ${DATA.prospectsState}`,
      })
    } catch {
      placeId = null
    }
    if (placeId === null || placeId === undefined) {
      placeId = selectedPlaceId
    } else {
      placeId = placeId[0]?.place_id
    }
    DATA.prospectsPlaceURL = `https://www.google.com/maps/place/?q=place_id:${placeId}`
    try {
      await run(client('demos/', {data: DATA}))
      setDemoRequestScheduled(true)
      setIsFormLoading(false)
      toast.success('Form submitted successfully')
    } catch (responseError) {
      setIsFormLoading(false)
      if (responseError.status !== 400) {
        throw responseError
      }
      if (
        responseError?.message &&
        responseError.message.includes('select a new date and time')
      ) {
        toast.error(responseError.message, {
          autoClose: false,
          closeOnClick: true,
          theme: 'colored',
        })
      } else {
        toast.error(responseError.message)
      }
    }
    setIsFormLoading(false)
  }

  const regions = useMemo(() => {
    return getRegionOptions(roles)
  }, [roles])

  useEffect(() => {
    if (
      watchProduct === 'Business Center' ||
      watchProduct === 'Marketing Center'
    ) {
      updateAvailableTimeOptions({
        name: 'product',
        value: watchProduct,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchProduct])

  useEffect(() => {
    updateAvailableTimeOptions({
      name: 'demoConductedInSpanish',
      value: demoLanguagePreference.value,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoLanguagePreference])

  useEffect(() => {
    updateAvailableAlignmentTimeOptions({
      name: 'demoConductedInSpanish',
      value: alignmentLanguagePreference.value,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alignmentLanguagePreference])

  useEffect(() => {
    const countrySelection = {
      name: IS_AUSTRALIA_USER
        ? 'Australia'
        : IS_NZ_USER
        ? 'New Zealand'
        : IS_INSIDE_USER
        ? 'Australia'
        : 'United States',
      value: IS_AUSTRALIA_USER
        ? 'AU'
        : IS_NZ_USER
        ? 'NZ'
        : IS_INSIDE_USER
        ? 'AU'
        : 'US',
    }
    setSelectedCountry(countrySelection)
    setValue('prospectsCountry', countrySelection)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IS_AUSTRALIA_USER, IS_NZ_USER, IS_INSIDE_USER])

  const [isUpdatingFromPlaceSearch, setIsUpdatingFromPlaceSearch] =
    useState(false)

  useEffect(() => {
    const contingentFields = [
      'prospectsMobilePhone',
      'contactNumber',
      'prospectsPostalCode',
    ]
    if (selectedCountry.value.length > 0) {
      getStates(selectedCountry.value)
    }
    contingentFields.forEach(field => {
      if (getValues(field)) {
        trigger(field)
      }
    })

    // Reset Spanish flag if country doesn't support Spanish
    if (selectedCountry.value !== 'US') {
      setValue('demoConductedInSpanish', false, {shouldValidate: false})
      setDemoLanguagePreference(RADIO_LANGUAGE_PREFERENCES[1])
      setAlignmentLanguagePreference(RADIO_LANGUAGE_PREFERENCES[1])
      setAvailableTimeOptions(prevOptions => ({
        ...prevOptions,
        demoConductedInSpanish: false,
      }))
      setAvailableAlignmentTimeOptions(prevOptions => ({
        ...prevOptions,
        demoConductedInSpanish: false,
      }))
    }

    // Only clear business information if NOT updating from place search
    if (!isUpdatingFromPlaceSearch) {
      setValue('prospectsStreet', '', {shouldValidate: false})
      setValue('prospectsCity', '', {shouldValidate: false})
      setValue('prospectsCompany', '', {shouldValidate: false})
      setProspectsCompanyName('')
      setValue('prospectsState', {}, {shouldValidate: false})
      setSelectedState({name: '', value: ''})
      setResetSelectedState(true)
      setValue('prospectsPostalCode', '', {shouldValidate: false})
      // Clear selected place ID so next search starts fresh
      setSelectedPlaceId(null)
      // Reset demo scheduler
      setDateTime(null)
      setSelectedHour(null)
      setDemoStartDateTime(null)
      setAlignmentStartDateTime(null)
      setAlignmentScheduledDateTime(null)
    } else {
      // Reset the flag after using it
      setIsUpdatingFromPlaceSearch(false)
    }
    // Reset selectedCenterType if Workforce Center is selected but country is not US
    if (selectedCenterType === 'wc' && selectedCountry.value !== 'US') {
      if (IS_PP_TSS_USER) {
        handleCenterTypeChange('keap,bc,mc')
      } else {
        handleCenterTypeChange('keap,bc')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry])

  useEffect(() => {
    if (demoRegionSelectionFlag) {
      const fetchUserRegion = async () => {
        const data = await client('user/region')
        if (data) {
          const regionId = LEGACY_REGIONS.find(
            region => region.name.toLowerCase() === data.toLowerCase(),
          )?.value
          if (!regionId) {
            return
          }
          setSelectedRegion({
            name: data,
            value: regionId,
          })
          setValue('prospectsRegion', {
            name: data,
            value: regionId,
          })
          setShowProspectRegionInput(false)
        }
      }
      fetchUserRegion()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client])

  useEffect(() => {
    setAvailableTimeOptions({
      ...availableTimeOptions,
      prospectsCountry: selectedCountry.name,
      regionId: selectedRegion.value,
    })
    setAvailableAlignmentTimeOptions({
      ...availableAlignmentTimeOptions,
      prospectsCountry: selectedCountry.name,
      regionId: selectedRegion.value,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion, selectedCountry])

  useEffect(() => {
    setAvailableTimeOptions({
      ...availableTimeOptions,
      isMarketingCenter: marketingCenterDemo,
    })
    setAvailableAlignmentTimeOptions({
      ...availableAlignmentTimeOptions,
      isMarketingCenter: marketingCenterDemo,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketingCenterDemo])

  useEffect(() => {
    if (prospectsCompanyName?.length > 3) {
      ;(async () => {
        // OOE-UI-G1/OOE-UI-F9: a Maps lookup that rejects (library still
        // loading, or a non-OK PlacesServiceStatus) had nothing to catch it in
        // this bare async IIFE and became an unhandled rejection. Company
        // autocomplete is a convenience, so degrade to "no suggestions" and
        // let the user keep typing.
        try {
          const predictions = await getPlacePredictionsDebounce({
            input: prospectsCompanyName,
            componentRestrictions: {country: ['us', 'ca', 'au', 'nz']},
          })
          let predictionsResult = []
          if (predictions) {
            predictionsResult = predictions.map(
              ({place_id: placeId, description}) => ({placeId, description}),
            )
          }
          setCompanyPredictions(predictionsResult)
        } catch {
          setCompanyPredictions([])
        }
      })()
    } else {
      setCompanyPredictions([])
    }
  }, [prospectsCompanyName])

  useEffect(() => {
    if (selectedPlaceId) {
      ;(async () => {
        // Rejections here (Maps not ready / non-OK status) previously escaped
        // this async IIFE, and a resolved-but-empty result blew up on
        // `.address_components`. Prefilling the form is best-effort.
        const placeInfo = await getPlaceDetails({
          placeId: selectedPlaceId,
          fields: PLACE_DETAIL_FIELDS,
        }).catch(() => null)
        if (!placeInfo) return
        const companyInfo = getCompanyLocationInfo(placeInfo.address_components)
        Object.keys(companyInfo).forEach(field => {
          if (field === 'prospectsState') {
            setSearchedState(companyInfo[field])
            return
          }
          if (field === 'prospectsCountry') {
            // Don't update country from search result - keep user's selected country
            // Just skip this field and let the user's selection remain
            return
          }
          setValue(field, companyInfo[field])
        })
        setProspectsCompanyName(placeInfo.name)
        setValue('prospectsCompany', placeInfo.name)
        // Trigger validation for postal code and other fields after they're set
        setTimeout(() => {
          trigger([
            'prospectsPostalCode',
            'prospectsStreet',
            'prospectsCity',
            'prospectsState',
          ])
        }, 0)
      })()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlaceId])

  useEffect(() => {
    if (!searchedState) return
    const stateObject = listOfStates.find(state => state.name === searchedState)
    if (!stateObject) {
      // If state not found in current country's list, clear it
      setSelectedState({name: '', value: ''})
      setValue('prospectsState', {}, {shouldValidate: false})
      return
    }
    setState(stateObject)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedState, listOfStates])

  const fetchCompanyDetails = async () => {
    const response = await client(`scans/${recordId}`)
    setValue('prospectsStreet', response.address)
    setValue('prospectsPostalCode', response.zip)
    setValue('prospectsCity', response.city)
    setValue('prospectsMobilePhone', response.business_phone)
    setProspectsCompanyName(response.business_name)
    setValue('prospectsCompany', response.business_name)
    const country = RADIO_COUNTRY_SELECTION.find(
      ({name}) => name === response.country,
    )
    setSelectedCountry(country)
    setValue('prospectsCountry', country)
    const state = LIST_OF_STATES.find(({name}) => name === response.state)
    if (state?.value) {
      setValue('prospectsState', state)
      setSelectedState(state)
    }
  }

  useEffect(() => {
    if (recordId) {
      fetchCompanyDetails()
    }
    console.log('roles', formattedRoles)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Set default radio button based on user role
    if (IS_INSIDE_USER || IS_AUSTRALIA_USER || IS_NZ_USER || IS_PP_TSS_USER) {
      handleCenterTypeChange('keap,bc,mc')
    } else if (IS_PPIL_USER) {
      handleCenterTypeChange('keap,bc')
    } else if (IS_PREMISE_USER) {
      handleCenterTypeChange('keap,bc')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleKeapDemoOnChange = () => {
    setValue('keapDemo', !keapDemo)
    setKeapDemo(!keapDemo)
    if (!keapDemo) {
      setSelectedCountry(RADIO_COUNTRY_SELECTION[0])
    }
  }

  useEffect(() => {
    if (keapDemo) {
      setValue('workforceCenterDemo', false)
      setWorkforceCenterDemo(false)
    }
    updateAvailableTimeOptions({
      name: 'isKeapDemo',
      value: keapDemo,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keapDemo])

  useEffect(() => {
    console.log('executed workforceCenterDemo')
    if (workforceCenterDemo) {
      setValue('keapDemo', false)
      setKeapDemo(false)
    }
    updateAvailableTimeOptions({
      name: 'isWorkforceCenterDemo',
      value: workforceCenterDemo,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workforceCenterDemo])

  // Removed useEffect that was hiding radio buttons for Premise users
  // Premise users should always see all radio button options

  if (demoRequestScheduled) {
    return (
      <>
        <MainContainer className="d-flex flex-column justify-content-center align-content-center">
          <div className="font-montserrat text-center">
            <p className="mb-4">
              {user.name}, we&apos;ve received your demo request for:
            </p>
            <h2>
              {dateTimeConverter(
                demoStartDateTime,
                selectedTimezone,
                'confirmation',
              )}
            </h2>
            <div className=" mb-2">
              <p className="m-1">{getValues('prospectsFirstName')}</p>
              <p className="m-1">{getValues('prospectsLastName')}</p>
              <p className="m-1">{getValues('prospectsMobilePhone')}</p>
              <p className="m-1">{getValues('prospectsEmailAddress')}</p>
            </div>
            {alignmentScheduledDateTime ? (
              <p>
                <b>
                  Your alignment call is scheduled for&nbsp;
                  {dateTimeConverter(
                    alignmentScheduledDateTime,
                    selectedTimezone,
                    'confirmation',
                  )}
                </b>
              </p>
            ) : null}
          </div>
          <div
            className="m-4 p-5"
            style={{backgroundColor: '#f2f3f4', borderRadius: '25px'}}
          >
            <h2 className="font-montserrat text-center">
              Recommendations for Demo
            </h2>
            <ol>
              <li className="font-normal font-montserrat text-thryv-black-500">
                1. Be early.
              </li>
              <li className="font-normal font-montserrat text-thryv-black-500">
                2. Connect to the internet.
              </li>
              <li className="font-normal font-montserrat text-thryv-black-500">
                3. Be on video & introduce the Software Sales Teammate.
              </li>
              <li className="font-normal font-montserrat text-thryv-black-500">
                4. Allow Software Sales Teammate to run the 45 minute
                experience.
              </li>
              <li className="font-normal font-montserrat text-thryv-black-500">
                5. Take notes on needs and be prepared to provide
                recommendations when demo is completed.
              </li>
            </ol>
          </div>
          <hr />
          <Button
            variant="primary"
            className="w-50 align-self-center"
            style={{maxWidth: '250px'}}
            onClick={() =>
              navigate('/demo-requests', {state: {activeTab: 'demo'}})
            }
          >
            Back to Demo Requests
          </Button>
        </MainContainer>
      </>
    )
  }

  return (
    <>
      <MainContainer>
        {isFormLoading ? (
          <ParagraphText variant="reg" className="h3 mb-5 text-center">
            Preparing your demo.
          </ParagraphText>
        ) : (
          <ParagraphText variant="reg" className="h3 mb-5 text-center">
            Tell us about your potential business
          </ParagraphText>
        )}
        {isFormLoading ? (
          <div className="d-flex justify-content-center">
            <SkeletonTheme color="#e6e6e6" highlightColor="#fff">
              <Skeleton
                containerTestId="new-demo-loader"
                height="70vh"
                width="70vw"
              />
            </SkeletonTheme>
          </div>
        ) : (
          <FormContainer>
            {IS_PPIL_USER &&
            !IS_INSIDE_USER &&
            !IS_AUSTRALIA_USER &&
            !IS_NZ_USER ? (
              <div className="d-flex gap-3">
                <Radio
                  hasLabel
                  id="keap-bc"
                  name="centerType"
                  value="keap,bc"
                  label={
                    <ParagraphText
                      variant="reg"
                      className="h7"
                      style={{fontWeight: 'bold'}}
                    >
                      {['AU', 'NZ', 'BB', 'KY'].includes(selectedCountry.value)
                        ? 'Business Center'
                        : 'Keap / Business Center'}
                    </ParagraphText>
                  }
                  toggled={true}
                  checked={selectedCenterType === 'keap,bc'}
                  className="font-weight-bold"
                  onChange={() => handleCenterTypeChange('keap,bc')}
                />
              </div>
            ) : IS_PP_TSS_USER ||
              IS_INSIDE_USER ||
              IS_AUSTRALIA_USER ||
              IS_NZ_USER ? (
              <div className="d-flex gap-3">
                <Radio
                  hasLabel
                  id="keap-bc-mc"
                  name="centerType"
                  value="keap,bc,mc"
                  label={
                    <ParagraphText
                      variant="reg"
                      className="h7"
                      style={{fontWeight: 'bold'}}
                    >
                      {['AU', 'NZ', 'BB', 'KY'].includes(selectedCountry.value)
                        ? 'Business Center / Marketing Center'
                        : 'Keap / Business Center / Marketing Center'}
                    </ParagraphText>
                  }
                  toggled={true}
                  checked={selectedCenterType === 'keap,bc,mc'}
                  className="font-weight-bold"
                  onChange={() => handleCenterTypeChange('keap,bc,mc')}
                />
              </div>
            ) : showRadioButtons ? (
              <div className="d-flex gap-3">
                <Radio
                  hasLabel
                  id="keap-bc"
                  name="centerType"
                  value="keap,bc"
                  label={
                    <ParagraphText
                      variant="reg"
                      className="h7"
                      style={{fontWeight: 'bold'}}
                    >
                      {['AU', 'NZ', 'BB', 'KY'].includes(selectedCountry.value)
                        ? 'Business Center'
                        : 'Keap / Business Center'}
                    </ParagraphText>
                  }
                  toggled={true}
                  checked={selectedCenterType === 'keap,bc'}
                  className="font-weight-bold"
                  onChange={() => handleCenterTypeChange('keap,bc')}
                />
              </div>
            ) : null}
            <div className="section-title">
              <ParagraphText variant="reg" className="h5">
                Client Information
              </ParagraphText>
            </div>
            <Input
              className="w-100"
              type="text"
              placeholder="First Name"
              aria-label="First Name"
              name="prospectsFirstName"
              withLabel
              labelType="floating"
              variant="default"
              register={register}
              errors={errors}
            />
            <Input
              className="w-100"
              type="text"
              placeholder="Last Name"
              aria-label="Last Name"
              name="prospectsLastName"
              withLabel
              labelType="floating"
              variant="default"
              register={register}
              errors={errors}
            />
            <Input
              className="w-100"
              type="text"
              placeholder="Mobile Phone"
              aria-label="Mobile Phone"
              name="prospectsMobilePhone"
              maxLength={18}
              withLabel
              labelType="floating"
              variant="default"
              register={register}
              errors={errors}
            />
            <Input
              className="w-100"
              type="text"
              placeholder="Email Address"
              aria-label="Email Address"
              name="prospectsEmailAddress"
              withLabel
              labelType="floating"
              variant="default"
              register={register}
              errors={errors}
            />
            <input type="hidden" {...register('businessOpportunity')} />
            <input type="hidden" {...register('currentSoftwareStack')} />
            <input type="hidden" {...register('timelineBuyingIntent')} />
            {!HIDE_SPECIAL_DEMOS && (
              <>
                <Checkbox
                  hasLabel
                  name="keapDemo"
                  label={
                    <ParagraphText
                      variant="reg"
                      className="h5"
                      style={{color: '#36A635', margin: 0}}
                    >
                      New Keap Demo
                    </ParagraphText>
                  }
                  value={keapDemo}
                  isChecked={keapDemo}
                  onChange={handleKeapDemoOnChange}
                />
              </>
            )}
            <div className="section-title">
              <ParagraphText
                variant="reg"
                className="h5"
                style={{marginBottom: '-1.5rem'}}
              >
                Select country
              </ParagraphText>
              <Controller
                control={control}
                name="prospectsCountry"
                render={({field: {onChange, name}}) => (
                  <RadioGroup
                    data={
                      workforceCenterDemo
                        ? [{name: 'United States', value: 'US'}]
                        : IS_PREMISE_USER && keapDemo
                        ? [
                            {name: 'United States', value: 'US'},
                            {name: 'Canada', value: 'CA'},
                          ]
                        : RADIO_COUNTRY_SELECTION
                    }
                    value={selectedCountry}
                    onChange={selectedObject => {
                      const selectedCountry = RADIO_COUNTRY_SELECTION.find(
                        option => option.value === selectedObject.value,
                      )
                      onChange(selectedCountry)
                      setSelectedCountry(selectedCountry)
                    }}
                    name={name}
                    row
                    layoutStyles={{
                      display: 'flex',
                      justifyContent: 'start',
                      marginLeft: '-5px',
                    }}
                    register={register}
                    disabled={workforceCenterDemo}
                  />
                )}
              />
            </div>
            <div className="section-title">
              <ParagraphText
                variant="reg"
                className="h5"
                style={{marginTop: '1rem'}}
              >
                Business Information
              </ParagraphText>
            </div>
            <div className="section-title">
              <Input
                className="w-100 span-cols search-input"
                type="text"
                placeholder="Business Name"
                aria-label="Business Name"
                name="prospectsCompany"
                withLabel
                variant="search"
                value={prospectsCompanyName}
                searchOptions={companyPredictions.map(p => p.description)}
                onChange={handleCompanySearch}
                onBlur={handleCompanySearchBlur}
                register={register}
                errors={errors}
              />
            </div>
            <div className="section-title">
              <Input
                className="w-100"
                type="text"
                placeholder="Address"
                aria-label="Address"
                name="prospectsStreet"
                withLabel
                labelType="floating"
                variant="default"
                register={register}
                errors={errors}
              />
            </div>
            <div className="section-title business-information-bottom">
              <Input
                className="business-information-input"
                type="text"
                placeholder="City"
                aria-label="City"
                name="prospectsCity"
                withLabel
                labelType="floating"
                variant="default"
                register={register}
                errors={errors}
              />
              <Controller
                control={control}
                name="prospectsState"
                render={({field: {onChange, name}}) => (
                  <Select
                    className="business-information-select"
                    options={listOfStates}
                    width="md"
                    selectLabel="State"
                    selectedOption={selectedState}
                    setSelectedOption={setState}
                    errors={errors}
                    onChange={onChange}
                    name={name}
                    cleared={resetSelectedState}
                    setCleared={setResetSelectedState}
                    withLabel={selectedState.value}
                    register={register}
                  />
                )}
              />
              <Input
                className="business-information-input"
                type="text"
                placeholder="Zip"
                aria-label="Zip"
                name="prospectsPostalCode"
                withLabel
                labelType="floating"
                variant="default"
                register={register}
                errors={errors}
              />
            </div>
            {demoRegionSelectionFlag &&
              showProspectRegionInput &&
              selectedCountry.value === 'US' &&
              !formattedRoles.includes('premisedemo') &&
              !formattedRoles.includes('tss') &&
              !formattedRoles.includes('admin') && (
                <div className="section-title">
                  <div className="select-container z-3 mb-3">
                    <Controller
                      control={control}
                      name="prospectsRegion"
                      render={({field: {onChange, name}}) => (
                        <Select
                          className="business-information-input"
                          options={regions}
                          width="md"
                          selectLabel="Region"
                          selectedOption={selectedRegion}
                          setSelectedOption={setSelectedRegion}
                          errors={errors}
                          onChange={onChange}
                          name={name}
                          withLabel={selectedRegion.value}
                          register={register}
                        />
                      )}
                    />
                  </div>
                </div>
              )}

            <div className="scheduler-container-wrap">
              <div className="scheduler-container">
                <ParagraphText variant="reg" className="h5 mb-3">
                  Demo Call
                </ParagraphText>
                {demoSchedulerEnabled ? (
                  <DateTimeModal
                    modalTitle="Appointment Scheduler"
                    buttonText={selectedHour ? 'Edit' : 'select date and time'}
                    modalId="new-demo-request-modal"
                    saveDateTime={saveDemoCallData}
                    availableTimeOptions={availableTimeOptions}
                    register={register}
                    errors={errors}
                    address={`${watchProspectsStreet} ${watchProspectsCity} ${watchProspectsState.value}`}
                    defaultDateTime={watchProspectsDate}
                    isUsSpanishOptionAvailable={isUsSpanishOptionAvailable}
                    callType="demo"
                    radioLanguageOptions={RADIO_LANGUAGE_PREFERENCES}
                    setLanguagePreference={setDemoLanguagePreference}
                    country={selectedCountry.value}
                    defaultDate={dateTime}
                    maxDate={addDays(new Date(), 7)}
                    minDate={new Date()}
                  />
                ) : (
                  <Button disabled={true} variant="primary">
                    select date and time (test)
                  </Button>
                )}
              </div>
              {SHOW_ALIGNMENT_CALL_MODAL && !workforceCenterDemo && (
                <div className="scheduler-container">
                  <>
                    <ParagraphText variant="reg" className="h5 mb-3">
                      Alignment Call
                    </ParagraphText>
                    {selectedHour ? (
                      <DateTimeModal
                        modalTitle="Alignment Call Scheduler"
                        buttonText={
                          alignmentStartDateTime
                            ? 'Edit'
                            : noAlignmentTimesAvailable
                            ? 'No Alignment Times Available'
                            : 'select date and time'
                        }
                        modalId="new-alignment-request-modal"
                        saveDateTime={saveAlignmentCallData}
                        availableTimeOptions={availableAlignmentTimeOptions}
                        isAlignmentCall={true}
                        alignmentCallTimesAvailable={true}
                        staffId={demoCallStaffId}
                        maxDate={dateTime}
                        defaultDate={dateTime}
                        selectedTimeStamp={selectedHour}
                        register={register}
                        disabled={!selectedHour}
                        errors={errors}
                        callType="alignment"
                        address={`${watchProspectsStreet} ${watchProspectsCity} ${watchProspectsState.value}`}
                        defaultDateTime={
                          watchAlignmentScheduledDateTime
                            ? dayjs(watchAlignmentScheduledDateTime).format(
                                'MMM-DD-YYYY HH:mm a',
                              )
                            : null
                        }
                        isUsSpanishOptionAvailable={isUsSpanishOptionAvailable}
                        radioLanguageOptions={RADIO_LANGUAGE_PREFERENCES}
                        setLanguagePreference={setAlignmentLanguagePreference}
                        country={selectedCountry.value}
                        keapDemo={keapDemo}
                        minDate={new Date()}
                      />
                    ) : (
                      <Button disabled={true} variant="primary">
                        select date and time
                      </Button>
                    )}
                  </>
                </div>
              )}
            </div>
            {['US', 'CA'].includes(selectedCountry.value) && (
              <>
                <div className="section-title" style={{marginTop: '1rem'}}>
                  <ParagraphText variant="reg" className="h5">
                    Business Opportunity
                  </ParagraphText>
                  <ParagraphText variant="reg" className="h7">
                    What challenge or opportunity is the customer trying to
                    solve? [Select All That Apply]
                  </ParagraphText>
                  <div className="d-flex flex-column gap-1 mt-2">
                    {BUSINESS_OPPORTUNITY_OPTIONS.map(opt => (
                      <Checkbox
                        hasLabel
                        key={opt}
                        name={`bo_${opt}`}
                        label={opt}
                        isChecked={(watchBusinessOpportunity || []).includes(
                          opt,
                        )}
                        onChange={() =>
                          toggleArrayValue('businessOpportunity', opt)
                        }
                      />
                    ))}
                    <Input
                      className="w-100"
                      type="text"
                      placeholder="Other"
                      aria-label="Business Opportunity Other"
                      name="businessOpportunityOther"
                      withLabel
                      labelType="floating"
                      variant="default"
                      register={register}
                      errors={errors}
                    />
                  </div>
                </div>

                <div className="section-title" style={{marginTop: '1rem'}}>
                  <ParagraphText variant="reg" className="h5">
                    Current Software Stack
                  </ParagraphText>
                  <ParagraphText variant="reg" className="h7">
                    What tools or systems are they currently using? [Select All
                    That Apply]
                  </ParagraphText>
                  <div className="d-flex flex-column gap-1 mt-2">
                    {CURRENT_SOFTWARE_OPTIONS.map(opt => (
                      <Checkbox
                        hasLabel
                        key={opt}
                        name={`css_${opt}`}
                        label={opt}
                        isChecked={(watchCurrentSoftwareStack || []).includes(
                          opt,
                        )}
                        onChange={() =>
                          toggleArrayValue('currentSoftwareStack', opt)
                        }
                      />
                    ))}
                    <Input
                      className="w-100"
                      type="text"
                      placeholder="Other"
                      aria-label="Current Software Other"
                      name="currentSoftwareStackOther"
                      withLabel
                      labelType="floating"
                      variant="default"
                      register={register}
                      errors={errors}
                    />
                  </div>
                </div>

                <div className="section-title" style={{marginTop: '1rem'}}>
                  <ParagraphText variant="reg" className="h5">
                    Timeline & Buying Intent
                  </ParagraphText>
                  <ParagraphText variant="reg" className="h7">
                    Where are they in their research or decision process?
                    [Select All That Apply]
                  </ParagraphText>
                  <div className="d-flex flex-column gap-1 mt-2">
                    {TIMELINE_INTENT_OPTIONS.map(opt => (
                      <Checkbox
                        hasLabel
                        key={opt}
                        name={`tbi_${opt}`}
                        label={opt}
                        isChecked={(watchTimelineBuyingIntent || []).includes(
                          opt,
                        )}
                        onChange={() =>
                          toggleArrayValue('timelineBuyingIntent', opt)
                        }
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
            <div className="section-title">
              <ParagraphText
                variant="reg"
                className="h5"
                style={{marginTop: '1.5rem'}}
              >
                What number can the demo team contact you?
              </ParagraphText>
            </div>
            <Input
              className="w-100 "
              type="text"
              placeholder="Phone Number"
              aria-label="Phone Number"
              name="contactNumber"
              maxLength={18}
              withLabel
              labelType="floating"
              variant="default"
              register={register}
              errors={errors}
            />
            <Input
              className="w-100"
              type="text"
              placeholder="Salesforce Customer ID (Optional)"
              aria-label="Salesforce Customer ID (Optional)"
              name="salesforceCustomerID"
              withLabel
              labelType="floating"
              variant="default"
              register={register}
              errors={errors}
            />
            <div className="span-cols input-group d-flex justify-content-center">
              <Button
                disabled={
                  !areQuestionsAnswered ||
                  !demoStartDateTime ||
                  (SHOW_ALIGNMENT_CALL_MODAL &&
                    !keapDemo &&
                    !workforceCenterDemo &&
                    !noAlignmentTimesAvailable &&
                    !alignmentStartDateTime)
                }
                variant="primary"
                type="submit"
                className="new-order-button d-flex justify-content-around align-items-center mr-4"
                onClick={() => {
                  console.log('Schedule Demo button clicked')
                  console.log('Form errors:', errors)
                  console.log('areQuestionsAnswered:', areQuestionsAnswered)
                  console.log('demoStartDateTime:', demoStartDateTime)
                  console.log('alignmentStartDateTime:', alignmentStartDateTime)
                  console.log(
                    'SHOW_ALIGNMENT_CALL_MODAL:',
                    SHOW_ALIGNMENT_CALL_MODAL,
                  )
                  console.log('keapDemo:', keapDemo)
                  console.log('workforceCenterDemo:', workforceCenterDemo)
                  handleSubmit(submitForm)()
                }}
              >
                Schedule Demo
              </Button>
            </div>
          </FormContainer>
        )}
      </MainContainer>
    </>
  )
}

export {NewDemo}
